<template>
  <figure ref="linkCard" class="card">
    <AppLink class="flex" :to="link">
      <img v-if="value?.img" :src="value.img" width="76" height="96" loading="lazy" />
      <figcaption class="content">
        <UiChip :label="t('linkCard.title')" :size="Size.S" />
        <div class="content-text">
          <span v-if="value?.title" class="title" v-html="value.title" />&nbsp;
          <span v-if="value?.subtitle" class="subtitle" v-html="value.subtitle" />
        </div>
        <div v-if="time" class="time">
          <IconBase name="icon-clock" color="--alias-color-icon-subtle" size="16" />
          <span class="time-text" v-html="time" />
        </div>
      </figcaption>
    </AppLink>
  </figure>
</template>

<script lang="ts" setup>
import { UiChip, Size } from '@experience/core-chips-ui';
import { useI18n } from '@experience/i18n';
import { computed } from 'vue';

import type { LinkedArticle } from '@/features/articleContent/models/LinkedArticle';
import IconBase from '@/shared/ui/iconBase/IconBase.vue';

import ru from './i18n/ru.json';

export interface Props {
  value: LinkedArticle;
}

const props = defineProps<Props>();

const { t } = useI18n<{ message: typeof ru }, 'ru'>({
  inheritLocale: true,
  messages: { ru },
});

const time = computed(() => {
  const seconds = props.value?.reading_time;

  return seconds ? t('linkCard.readingTime', { minutes: Math.round(seconds / 60) }) : undefined;
});
const link = computed(() => `/journal/articles/${props.value?.slug}/`);
</script>

<style lang="scss" scoped>
.card {
  display: flex;
  width: 495px;
  padding: var(--alias-space-32, 32px) 0;

  @include mobile {
    width: 100%;
    padding: var(--alias-space-24, 24px) 0;
  }
}

.flex {
  display: flex;
}

img {
  cursor: pointer;
  object-fit: cover;
  border-radius: 8px;
}

.content {
  margin-left: 16px;
  cursor: pointer;
}

.chips {
  padding: 0 6px;
  font-size: 14px;
  line-height: 20px;
  text-align: center;
  background-color: var(--alias-color-background-subtler);
  border-radius: 4px;
}

.content-text,
.time {
  margin-top: 4px;
}

.time {
  display: flex;
  align-items: center;
  color: var(--alias-color-text-subtle);
  fill: var(--alias-color-icon-subtle);
}

.title,
.subtitle {
  font-size: 14px;
  line-height: 20px;
}

.title {
  font-weight: 700;
}

.time-text {
  margin-left: 4px;
  font-size: 14px;
  line-height: 20px;
  color: var(--alias-color-text-subtle);
}

a {
  color: var(--alias-color-text-default);
  text-decoration: none;
}
</style>
